import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import {
  createSessionNote,
  removeSessionNotePhoto,
  updateSessionNote,
  uploadSessionNotePhoto,
} from '@/api/sessionNotes/sessionNotesApi';
import type {
  SessionNote,
  SessionNoteWritePayload,
} from '@/api/sessionNotes/sessionNotesApi.types';
import {
  APPOINTMENTS_QUERY_KEY,
  buildDeviceSettingsHistoryQueryKey,
  buildPatientPerformedTreatmentsQueryKey,
  buildPatientQueryKey,
  buildPatientSessionNotesQueryKey,
  buildPatientTimelineQueryKey,
  buildSessionNoteQueryKey,
} from '@/constants/queryKeys';

export function useSessionNoteInvalidation() {
  const queryClient = useQueryClient();
  return function invalidate(note: SessionNote) {
    queryClient.invalidateQueries({ queryKey: buildSessionNoteQueryKey(note.id) });
    queryClient.invalidateQueries({ queryKey: buildPatientSessionNotesQueryKey(note.patientId) });
    queryClient.invalidateQueries({ queryKey: buildPatientTimelineQueryKey(note.patientId) });
    queryClient.invalidateQueries({
      queryKey: buildDeviceSettingsHistoryQueryKey(note.patientId),
    });
    queryClient.invalidateQueries({
      queryKey: buildPatientPerformedTreatmentsQueryKey(note.patientId),
    });
    queryClient.invalidateQueries({ queryKey: buildPatientQueryKey(note.patientId) });
    queryClient.invalidateQueries({ queryKey: APPOINTMENTS_QUERY_KEY });
  };
}

interface CreateSessionNoteVariables {
  patientId: string;
  payload: SessionNoteWritePayload;
}

export function useCreateSessionNoteMutation(): UseMutationResult<
  SessionNote,
  Error,
  CreateSessionNoteVariables
> {
  const invalidate = useSessionNoteInvalidation();
  return useMutation({
    mutationFn: function create(variables: CreateSessionNoteVariables) {
      return createSessionNote(variables.patientId, variables.payload);
    },
    onSuccess: invalidate,
  });
}

interface UpdateSessionNoteVariables {
  noteId: string;
  payload: SessionNoteWritePayload;
}

export function useUpdateSessionNoteMutation(): UseMutationResult<
  SessionNote,
  Error,
  UpdateSessionNoteVariables
> {
  const invalidate = useSessionNoteInvalidation();
  return useMutation({
    mutationFn: function update(variables: UpdateSessionNoteVariables) {
      return updateSessionNote(variables.noteId, variables.payload);
    },
    onSuccess: invalidate,
  });
}

interface UploadPhotoVariables {
  noteId: string;
  file: File;
}

export function useUploadSessionNotePhotoMutation(): UseMutationResult<
  SessionNote,
  Error,
  UploadPhotoVariables
> {
  const invalidate = useSessionNoteInvalidation();
  return useMutation({
    mutationFn: function upload(variables: UploadPhotoVariables) {
      return uploadSessionNotePhoto(variables.noteId, variables.file);
    },
    onSuccess: invalidate,
  });
}

interface RemovePhotoVariables {
  noteId: string;
  photoId: string;
}

export function useRemoveSessionNotePhotoMutation(): UseMutationResult<
  SessionNote,
  Error,
  RemovePhotoVariables
> {
  const invalidate = useSessionNoteInvalidation();
  return useMutation({
    mutationFn: function remove(variables: RemovePhotoVariables) {
      return removeSessionNotePhoto(variables.noteId, variables.photoId);
    },
    onSuccess: invalidate,
  });
}
